import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  BriefcaseIcon,
  UserIcon,
  CalendarIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { formatDateShort } from "@/shared/core";
import type { CaseWithDetails } from '../types/case.types';

interface CaseCardProps {
  case: CaseWithDetails;
  onClick?: () => void;
}

export const CaseCard: React.FC<CaseCardProps> = ({
  case: caseData,
  onClick,
}) => {
  const { t } = useTranslation();

  const getStatusGradient = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-linear-to-r from-amber-400 to-orange-500 text-white";
      case "active":
        return "bg-linear-to-r from-emerald-400 to-green-500 text-white";
      case "closed":
        return "bg-linear-to-r from-slate-400 to-gray-500 text-white";
      case "archived":
        return "bg-linear-to-r from-tiber-400 to-tiber-500 text-white";
      default:
        return "bg-linear-to-r from-amber-400 to-orange-500 text-white";
    }
  };

  const getPriorityGradient = (priority: string) => {
    switch (priority) {
      case "low":
        return "bg-linear-to-r from-gray-400 to-slate-500 text-white";
      case "medium":
        return "bg-linear-to-r from-tiber-400 to-tiber-500 text-white";
      case "high":
        return "bg-linear-to-r from-orange-400 to-red-500 text-white";
      case "urgent":
        return "bg-linear-to-r from-red-500 to-rose-600 text-white";
      default:
        return "bg-linear-to-r from-tiber-400 to-tiber-500 text-white";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className="group bg-white rounded-xl p-6 shadow-lg border border-tiber-600 hover:shadow-2xl transition-all duration-300 cursor-pointer"
    >
      {/* Card Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 rounded-lg bg-linear-to-br from-emerald-500 to-green-600">
              <BriefcaseIcon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-tiber-900 truncate group-hover:text-tiber-400 transition-colors">
                {caseData.title}
              </h3>
              <p className="text-sm text-tiber-600 font-mono mt-1">
                {caseData.case_number}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getStatusGradient(
                caseData.status
              )}`}
            >
              {t(`lawyer.caseCard.status.${caseData.status}`)}
            </span>
            {caseData.priority && (
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getPriorityGradient(
                  caseData.priority
                )}`}
              >
                {t(`lawyer.caseCard.priority.${caseData.priority}`)}
              </span>
            )}
            {caseData.practice_area && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-tiber-100 text-tiber-700">
                {caseData.practice_area}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="mb-4">
          <div className="flex items-center gap-4 text-sm text-tiber-600 mb-3 flex-wrap">
          {caseData.client_profiles?.profiles && (
            <>
              <div className="flex items-center gap-2">
                <UserIcon className="w-4 h-4" />
                <span className="line-clamp-1">
                  {caseData.client_profiles.profiles.full_name}
                </span>
              </div>
              <span className="text-tiber-300">•</span>
            </>
          )}
          <div className="flex items-center gap-1">
            <CalendarIcon className="w-4 h-4" />
            <span>{formatDateShort(caseData.created_at)}</span>
          </div>
          {caseData.documents_count !== undefined && (
            <>
              <span className="text-tiber-300">•</span>
              <span className="text-xs">
                {caseData.documents_count} {t('lawyer.cases.documents')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Card Footer */}
      <div className="flex items-center justify-end pt-4 border-t border-tiber-100">
        <ChevronRightIcon className="h-5 w-5 text-tiber-400 group-hover:text-tiber-400 transition-colors" />
      </div>
    </motion.div>
  );
};
